<template>
  <v-dialog v-model="dialog" max-width="620" scrollable persistent>
    <template #activator="{ on }">
      <slot name="activator" :on="on" />
    </template>

    <v-card>
      <v-card-title>Converti in ordine</v-card-title>

      <v-card-text style="max-height: 70vh">
        <!-- Sezione quantità per articolo/colore -->
        <div class="d-flex align-center mb-1">
          <v-subheader class="px-0">Quantità da includere in questo ordine</v-subheader>
          <v-spacer />
          <v-btn x-small text color="grey darken-1" @click="azzera_qta">Azzera tutto</v-btn>
        </div>
        
        <div v-for="art in articoli_conv" :key="art.art_id" class="mb-4">
          <div class="subtitle-2 mb-2">{{ art.nome }}</div>

          <v-row
            v-for="colore in art.colori"
            :key="colore.id"
            no-gutters
            align="center"
            class="mb-2"
          >
            <v-col cols="1">
              <v-avatar size="30">
                <v-img :src="color_img_by_art(art.art_id, colore.id)" />
              </v-avatar>
            </v-col>
            <v-col cols="6" class="caption pl-2">
              <span v-if="colore.nome_serie" class="grey--text mr-1">{{ colore.nome_serie }}</span>
              {{ colore.nome }}
            </v-col>
            <v-col cols="2">
              <v-text-field
                v-model.number="colore._qta"
                type="number"
                :min="0"
                :max="colore._qta_max"
                dense
                outlined
                hide-details
                style="max-width: 90px"
                @change="clamp_qta(colore)"
              />
            </v-col>
            <v-col cols="2" class="caption grey--text text-right  pr-2">
              <span style="font-size: 20px;">/ {{ colore._qta_max }}</span>
            </v-col>
            
          </v-row>

          <v-divider class="mt-2" />
        </div>

        <v-alert v-if="articoli_conv.length === 0" type="warning" dense text>
          Nessun articolo disponibile nella prenotazione.
        </v-alert>

        <!-- Tipo consegna + note -->
        <v-radio-group v-model="ritiro_cod" row label="Tipo consegna" class="mt-4">
          <v-radio label="Ritiro in azienda" :value="0" />
          <v-radio label="Consegna a domicilio" :value="1" />
        </v-radio-group>

        <v-textarea
          v-model="commento"
          label="Note aggiuntive per l'ordine (opzionale)"
          outlined
          dense
          rows="2"
          auto-grow
        />

        <!-- Risultato conversione -->
        <v-alert v-if="ordine_creato" type="success" dense text>
          Ordine #{{ ordine_creato.numero_ordine }} creato con successo.
          <router-link :to="'/ordini'" class="ml-1">Vai agli ordini →</router-link>
        </v-alert>

        <v-alert v-if="error" type="error" dense text>
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="loading" @click="chiudi">
          {{ ordine_creato ? "Chiudi" : "Annulla" }}
        </v-btn>
        <v-btn
          v-if="!ordine_creato"
          color="success"
          :loading="loading"
          :disabled="!ha_articoli_validi"
          @click="converti"
        >
          Converti
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ConvertOrdineDialog",

  props: {
    prenotazione: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      dialog: false,
      ritiro_cod: 0,
      commento: "",
      loading: false,
      error: null,
      ordine_creato: null,
      articoli_conv: [],
    };
  },

  computed: {
    prodotti_map() {
      const map = {};
      (this.$store.getters.get_lista_prodotti_raw || []).forEach((p) => {
        map[p.id] = p;
      });
      return map;
    },

    articoli_da_convertire() {
      return this.articoli_conv
        .map((a) => ({
          id_prodotto: a.art_id,
          colori: a.colori
            .filter((c) => c._qta > 0)
            .map((c) => ({ id_colore: c.id, qta: Math.min(c._qta, c._qta_max) })),
        }))
        .filter((a) => a.colori.length > 0);
    },

    ha_articoli_validi() {
      return this.articoli_da_convertire.length > 0;
    },
  },

  watch: {
    dialog(val) {
      if (val) this.init();
    },
  },

  methods: {
    init() {
      this.articoli_conv = (this.prenotazione.articoli || [])
        .filter((art) => !art.eliminato)
        .map((art) => ({
          art_id: art.art,
          nome: art.nome,
          colori: (art.colori || [])
            .filter((c) => !c.eliminato && c.numero > 0)
            .map((c) => ({
              id: c.id,
              nome: c.nome,
              nome_serie: c.nome_serie || "",
              _qta: c.numero,
              _qta_max: c.numero,
            })),
        }))
        .filter((a) => a.colori.length > 0);
    },

    azzera_qta() {
      for (const art of this.articoli_conv) {
        for (const colore of art.colori) {
          colore._qta = 0;
        }
      }
    },

    clamp_qta(colore) {
      if (colore._qta > colore._qta_max) colore._qta = colore._qta_max;
      if (colore._qta < 0 || isNaN(colore._qta)) colore._qta = 0;
    },

    color_img_by_art(art_id, colore_id) {
      const p = this.prodotti_map[art_id];
      if (p?.specie) return process.env.VUE_APP_API_ROOT + "/specie/img_colore/" + colore_id;
      return process.env.VUE_APP_API_ROOT + "/prodotto/" + art_id + "/img_colore/" + colore_id;
    },

    async converti() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.$store.dispatch("prenotazioni/converti", {
          id: this.prenotazione._id.$oid,
          payload: {
            ritiro_cod: this.ritiro_cod,
            commento: this.commento,
            articoli_override: this.articoli_da_convertire,
          },
        });
        this.ordine_creato = res;
        this.$store.dispatch("fetch_ordini_attivi");
        // Verifica se la prenotazione è ora completamente convertita (stato 2)
        // dopo che Vuex ha aggiornato lo store
        await this.$nextTick();
        if (this.prenotazione.stato === 2) {
          this.$emit("convertita", res);
        }
      } catch (e) {
        const code = e?.response?.data?.error;
        const messaggi = {
          solo_prenotazioni_confermate:
            "La prenotazione deve essere in stato Confermata per essere convertita.",
          gia_convertita: "Questa prenotazione è già stata convertita.",
          prenotazione_vuota: "La prenotazione non contiene articoli.",
          nessun_articolo_valido:
            "Nessun articolo valido trovato (prodotti non più disponibili).",
          qta_supera_prenotazione:
            "Le quantità indicate superano quelle prenotate.",
        };
        this.error =
          messaggi[code] ?? "Errore durante la conversione. Riprova.";
      } finally {
        this.loading = false;
      }
    },

    chiudi() {
      this.dialog = false;
      this.ritiro_cod = 0;
      this.commento = "";
      this.error = null;
      this.ordine_creato = null;
      this.articoli_conv = [];
    },
  },
};
</script>

